import React, { useState } from 'react';
import { XIcon, SearchIcon, BriefcaseIcon, PlusIcon } from 'lucide-react';
import { useActiveServices } from '../../hooks/useServices';
import type { Service } from '../../types/services';

interface ServicePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (service: Service) => void;
}

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ServicePicker({ isOpen, onClose, onSelect }: ServicePickerProps) {
  const { data, isLoading, isError } = useActiveServices();
  const [searchQuery, setSearchQuery] = useState('');

  const services = data?.services ?? [];

  const filteredServices = searchQuery.trim()
    ? services.filter((s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : services;

  const handleSelect = (service: Service) => {
    onSelect(service);
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-fade-in flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#212121]">Select Service</h2>
          <button onClick={onClose} className="text-gray-500">
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
        </div>

        {/* Services List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="py-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B8E87]"></div>
            </div>
          ) : isError ? (
            <div className="py-4 text-center text-red-500">
              Failed to load services
            </div>
          ) : services.length === 0 ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BriefcaseIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2">No services defined yet</p>
              <p className="text-sm text-gray-500">
                Go to Profile → My Services to add services
              </p>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No services match your search
            </div>
          ) : (
            <div className="space-y-2">
              {filteredServices.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleSelect(service)}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-[#5B8E87] hover:bg-[#5B8E87]/5 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-[#212121]">{service.name}</h4>
                    {service.description && (
                      <p className="text-sm text-gray-500 truncate">
                        {service.description}
                      </p>
                    )}
                  </div>
                  <span className="text-lg font-semibold text-[#5B8E87] ml-4">
                    {formatCurrency(service.default_price)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
